import { ApiService } from '../../services/api_service';
import btnSty from '../../core/elements/buttons.less';
import { ContextService } from '../../services/context_service';
import { Document, Font, Page, Text, View } from '@react-pdf/renderer';
import { GetLayoutStyle } from '../zusatztools/pdf/layout';
import { getReportConfigItem } from './helper/getReportConfigs';
import { getStyle } from './generisch/DatanauswertungGenerischStyles';
import HTMLtoDOCX from 'html-to-docx';
import { i18n } from '../../i18n/i18n';
import { lazyInject } from '../../services/inversify';
import { Md5 } from 'ts-md5/dist/md5';
import * as Moment from 'moment';
import { pdf } from '@react-pdf/renderer';
import * as React from 'react';
import ReactDOMServer from 'react-dom/server';
import { renderBanner } from './generisch/renderBanner';
import { renderDiagram } from './generisch/renderDiagram';
import { renderDiagramPdf } from './generisch/renderDiagramPdf';
import { renderDocxDiagram, renderDocxFooter, renderDocxHeader, renderDocxRows } from './generisch/renderDocx';
import { renderHeader, renderTitle } from './generisch/renderHeader';
import { rowHasValues, renderRows } from './generisch/renderRows';
import { svgToPng } from '../helper';
import { WActionButton } from '../../core/elements/WActionButton';
import { WSpinner } from '../../core/elements/spinner/WSpinner';
import { WpdfView } from './pdf/WpdfView';

interface DatenauswertungGenerischProps extends React.ClassAttributes<any> {
    config: any;
    orientation: any;
    setRenderButton?: (button: JSX.Element) => void;
    funcExternalElementUnderTable?: (isPdf: boolean, values: any) => JSX.Element;
    funcExternalElementExtrapage?: (isPdf: boolean, values: any) => JSX.Element | boolean;
    chartOnly?: boolean;
    contentOnly?: boolean;
    fontFactor?: number;
    pdfButtonStyle?: React.CSSProperties;
    pdfLayoutless?: boolean;
    setJSXdirect?: (content: JSX.Element) => void;
    callbackPages?: (pageNumber: number, totalPages: number) => void;
    docxExport?: boolean;
    id?: string;
}

interface DatenauswertungGenerischState {
    config: any;
    lastReportMd5: string;
    pdfString?: string;
    showSpinner: boolean;
    docType: 'docx' | 'pdf';
}

export class DatenauswertungGenerisch extends React.Component<DatenauswertungGenerischProps, DatenauswertungGenerischState> {

    constructor(props: any) {
        super(props);
        this.state = {
            config: null,
            lastReportMd5: null,
            showSpinner: false,
            docType: this.props.setJSXdirect ? 'pdf' : null
        };
        this.createDiagrammSvg = this.createDiagrammSvg.bind(this);
        this.ignoreZeroData = this.ignoreZeroData.bind(this);
        this.processPDF = this.processPDF.bind(this);
        this.renderPDF = this.renderPDF.bind(this);
        this.renderDocx = this.renderDocx.bind(this);
    }

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(GetLayoutStyle) private getLayoutStyle: GetLayoutStyle;

    private report: any = {};

    componentDidMount(): void {
        this.ignoreZeroData();

        if (this.props.setRenderButton) {
            this.props.setRenderButton(this.getRenderButton());
        }

        if (this.props.setJSXdirect) {
            this.report = this.getReportConfig();
        }
    }

    componentDidUpdate(prevProps: any): void {
        if (this.props.config && this.props.config.rows !== prevProps.config.rows) {
            this.ignoreZeroData();
        }

        if (!this.props.setJSXdirect && this.getReportMd5() !== this.state.lastReportMd5) {
            this.setState(
                { lastReportMd5: this.getReportMd5() },
                () => this.report = this.getReportConfig()
            );
        }
    }

    async createDiagrammSvg(): Promise<void> {
        const { config } = this.props;

        const graph = document.getElementsByClassName('recharts-responsive-container');
        const graphs: any[] = await [];
        const pngLinks: any[] = [];
        const diagramSize: any[] = [];
        const diagramStyle = getStyle('diagrammWrapper');

        Array.from(graph).forEach((item: any) => {
            if ((!this.props.id || item.id === this.props.id) && (item.children[0] || {}).children[0]) {
                graphs.push(item.children[0].children[0]);
            }
        });

        if (graphs.length === 0) {
            if (this.state.docType === 'docx') {
                this.renderDocx(pngLinks);
            } else if (this.state.docType === 'pdf') {
                this.renderPDF(pngLinks, diagramSize);
            }
        }

        graphs.forEach(async (item: any, idx: number) => {
            item.style.fontSize = diagramStyle.fontSize;
            item.style.fontFamily = diagramStyle.fontFamily;
            const itemData = item.getBoundingClientRect();
            const svg = new XMLSerializer().serializeToString(item);
            // const svgBlob = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
            // image/svg+xml;charset=utf-8 geht nicht bei Safari
            // image/svg+xml -> der richtiger Typ
            const svgBlob = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
            let pngLink: any;

            if (config.diagrams && config.diagrams[idx] && config.diagrams[idx].type === 'PieChart' && config.diagrams[idx].layout === 'row') {
                // PieChart
                const rad = itemData.width;
                pngLink = await svgToPng(svgBlob, rad, rad, 3);
            } else {
                pngLink = await svgToPng(svgBlob, itemData.width, itemData.height, 3);
            }
            diagramSize.push({ width: itemData.width, height: itemData.height });
            pngLinks.push(pngLink);

            if (idx + 1 === graphs.length) {
                if (this.state.docType === 'docx') {
                    this.renderDocx(pngLinks);
                } else if (this.state.docType === 'pdf') {
                    this.renderPDF(pngLinks, diagramSize);
                }
            }
        });

    }

    getRenderButton(): JSX.Element {
        return <div style={{ display: 'flex', flexDirection: 'row' }}>
            <WActionButton
                label={i18n('PDF generieren', 'sys')}
                // insideText={i18n('PDF generieren', 'sys')}
                tooltip={i18n('PDF generieren', 'sys')}
                key='pdf'
                action='print'
                styleProps={this.props.pdfButtonStyle}
                onAction={() => this.setState({ showSpinner: true, docType: 'pdf' }, this.createDiagrammSvg)}
            />
            {(this.apiService.getUsername() === 'admin' || this.apiService.getUsername() === 'yang.wang@wips-home.de') && this.props.docxExport &&
                <WActionButton
                    label={i18n('DOCX generieren', 'sys')}
                    insideText={i18n('DOCX generieren', 'sys')}
                    key='word'
                    action='word_white'
                    styleProps={this.props.pdfButtonStyle}
                    onAction={() => this.setState({ showSpinner: true, docType: 'docx' }, this.createDiagrammSvg)}
                />}
        </div>;
    }

    getReportConfig(): any {
        const contextReport: any = this.contextService.get('report_config') || {};
        const data: any[] = contextReport.useContext && Object.keys(contextReport.config || {}).length
            ? contextReport.config : null;
        const report: any = { ...((this.props.config || {}).report || {}), ...(data && { data: [{ konfiguration: data }] }) };

        return getReportConfigItem(report) || {};
    }

    getReportMd5(): string {
        return Md5.hashAsciiStr(JSON.stringify(this.getReportConfig())).toString();
    }

    ignoreZeroData(): void {
        const config: any = this.props.config || {};
        const diagrams: any[] = [];

        (config.diagrams || []).forEach((diagram: any, index: number) => {
            const legend: any[] = [...diagram.legend || []];
            const lineColor: string[] = [...diagram.lineColor || []];
            const unit: any[] = [...diagram.unit || []];
            const Y_dataKey: any[] = [...(diagram.Y_dataKey || [])];
            diagrams.push({ ...diagram });

            if (!diagram.ignoreZeroData) {
                return;
            }
            const data: any[] = diagram.data || [];
            const last: number = data.length - 1;
            const values: any = {};

            (diagram.Y_dataKey || []).forEach((key: string, idx: number) => {
                values[key] = { idx, sum: 0 };
            });

            data.forEach((item: any, index2: number) => {
                Object.keys(values).forEach((key: string) => {
                    values[key].sum += item[key] || 0;

                    if (index2 === last && !values[key].sum) {
                        delete legend[values[key].index];
                        delete lineColor[values[key].index];
                        delete unit[values[key].index];
                        delete Y_dataKey[values[key].index];
                    }
                });
            });
            diagrams[index].legend = legend.filter((element: string) => element !== undefined);
            diagrams[index].lineColor = lineColor.filter((element: string) => element !== undefined);
            diagrams[index].unit = unit.filter((element: string) => element !== undefined);
            diagrams[index].Y_dataKey = Y_dataKey.filter((element: string) => element !== undefined);
        });
        this.setState({ config: { ...config, diagrams } }, () => {
            if (this.props.setJSXdirect) {
                this.createDiagrammSvg();
            }
        });
    }

    async processPDF(pdfData: JSX.Element): Promise<void> {
        // console.log(pdfData);
        if (this.props.setJSXdirect) {
            this.props.setJSXdirect(pdfData);
            return;
        }
        pdfData = <Document>{pdfData}</Document>;
        try {
            const blob = await pdf(pdfData).toBlob();
            // console.log('PDF-Blob done');
            const pdfString = URL.createObjectURL(blob);
            const filename = this.state.config.filename ? this.state.config.filename : 'Auswertung.pdf';
            // const a = document.createElement('a');
            // a.title = filename;
            // a.href = pdfString;
            // a.download = filename;
            // document.body.appendChild(a);
            // a.click();
            // document.body.removeChild(a);
            saveAs(pdfString, filename);
        } catch (error) {
            // console.log('error: ', error);
        }
    }

    render(): JSX.Element {
        if (!this.state.config) {
            return null;
        }
        const { config } = this.state;
        if (this.props.setJSXdirect) {
            // Wir wollen nur rendern, nix ausgeben
            //  return <div></div>;
        }
        const flexRow = config && config.diagramsPosition === 'right' ? true : false;

        if (this.props.chartOnly) {
            const style: any = { diagrammWrapper: { padding: 'unset' } };
            return <div>{config.diagrams && renderDiagram(config.diagrams, style, this.props.id)}</div>;
        }

        return (
            <>
                <div style={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'flex-end' }}>
                    {!this.props.setRenderButton && this.getRenderButton()}
                </div>
                <div
                    style={{
                        ...(flexRow && { display: 'flex', flexDirection: 'row' }),
                        ...{ width: config && config.width > 0 ? config.width : 1400 }, marginLeft: -20
                    }}
                >
                    <div style={{ flex: 1, ...(config.tableStyle && config.tableStyle) }}>
                        {this.state.pdfString &&
                            <a
                                href={this.state.pdfString}
                                target='_blank'
                                download='Demo.pdf'
                            >
                                <button className={btnSty.textButton}>Ansehen / Speichern</button>
                            </a>
                        }
                        {this.renderContent(this.props.fontFactor)}
                        {this.props.funcExternalElementUnderTable &&
                            <div>{this.props.funcExternalElementUnderTable(false, config.values)}</div>
                        }
                        {this.props.funcExternalElementExtrapage &&
                            <div>{this.props.funcExternalElementExtrapage(false, config.values)}</div>
                        }
                    </div>
                    <div style={{ display: 'flex', flexDirection: (config.diagramLayout === 'column' ? 'column' : 'row') }}>
                        {Array.isArray(config.diagrams) && config.diagrams.map((diagram: any, index: number) =>
                            <div
                                key={`diagram-${index}`}
                                style={{ flex: 1, ...(flexRow && { marginTop: 20 }), ...(config.diagramsStyle && config.diagramsStyle) }}
                            >
                                {renderDiagram(
                                    [diagram],
                                    config.diagrams.length > 1 && config.diagramLayout !== 'column'
                                        ? { diagrammWrapper: { ...(index ? { paddingLeft: 10 } : { paddingRight: 10 }) }} : {},
                                    this.props.id
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {this.state.showSpinner && <WSpinner label={this.state.docType === 'docx' ? 'DOCX...' : 'PDF...'} />}
            </>
        );
    }

    renderContent(fontFactor?: number): JSX.Element {
        const header: any = (this.props.config || {}).header || {};
        const isPDF = this.state.docType === 'pdf';
        return (
            <WpdfView style={{ ...getStyle('main', isPDF), ...(!header.title && { borderRadius: 'unset'}) }} pdf={isPDF}>
                {renderTitle(this.state.config.header, isPDF, fontFactor, this.state.config)}
                {renderHeader(this.state.config.header, isPDF, fontFactor, this.state.config)}
                {renderRows(this.state.config, isPDF, fontFactor, !!this.props.setJSXdirect)}
            </WpdfView>
        );
    }

    renderFooter(): JSX.Element {
        return (
            <>
                <Text style={getStyle('footer')} fixed>{Moment().format('DD.MM.YYYY')}</Text>
                <View style={getStyle('footerPages')} fixed>
                    <Text
                        render={({ pageNumber, totalPages }) => {
                            if (typeof this.props.callbackPages === 'function' && totalPages) {
                                this.props.callbackPages(pageNumber, totalPages);
                            }

                            if (totalPages > 1) {
                                return `${pageNumber}/${totalPages}`;
                            }
                        }}
                        fixed
                    />
                </View>
            </>
        );
    }

    async renderPDF(pngLinks: any[], diagramSize: any[]): Promise<void> {
        const styles: any = this.getLayoutStyle.styles();
        // Lade Fonts
        const fontCalibri = require('../../font/calibri_regular.ttf');
        const fontCalibriBold = require('../../font/calibri_bold.ttf');
        Font.register({ family: 'Calibri', src: fontCalibri });
        Font.register({ family: 'Calibri-Bold', src: fontCalibriBold });
        const { diagrams, diagramLayout } = this.state.config;
        const page = 0;
        const pageStyle: any = { padding: 40, paddingTop: 20, paddingBottom: 25 };
        const rows = this.state.config.rows;
        const dataRows: any[] = (rows || []).filter((item: any[]) => rowHasValues(item));
        const hasPage2 = rows && dataRows.length > 10 && diagrams && diagrams.length > 0;
        let hasPage3 = false;
        if (this.props.funcExternalElementExtrapage && this.props.funcExternalElementExtrapage(true, this.state.config.values)) {
            hasPage3 = true;
        }
        let pdfData;
        if (this.props.chartOnly) {
            pdfData = renderDiagramPdf(pngLinks, diagramSize, diagrams, diagramLayout);
        } else if (this.props.contentOnly) {
            pdfData = this.renderContent();
        } else if (this.props.pdfLayoutless) {
            pdfData = <>
                <View key={`page-${page}`}>
                    {this.renderContent()}
                    {!hasPage2 && <>
                        {renderDiagramPdf(pngLinks, diagramSize, diagrams, diagramLayout)}
                        {this.renderReportBemerkung()}
                    </>}
                    {this.props.funcExternalElementUnderTable &&
                        <View>
                            {this.props.funcExternalElementUnderTable(true, this.state.config.values)}
                        </View>
                    }
                </View>
                {hasPage2 &&
                    <View key={`page-${page + 1}`} break>
                        {renderDiagramPdf(pngLinks, diagramSize, diagrams, diagramLayout)}
                        {this.renderReportBemerkung()}
                    </View>
                }
                {hasPage3 &&
                    <View key={`page-${page + 2}`} break>
                        {this.props.funcExternalElementExtrapage(true, this.state.config.values)}
                    </View>
                }
            </>;
        } else {
            pdfData = <>
                <Page size='A4' orientation={this.props.orientation} style={pageStyle} key={`page-${page}`}>
                    {renderBanner(this.state.config, styles, this.contextService, this.props.orientation)}
                    {this.renderContent()}
                    {this.props.funcExternalElementUnderTable &&
                        <View>
                            {this.props.funcExternalElementUnderTable(true, this.state.config.values)}
                        </View>
                    }
                    {!hasPage2 &&
                        <>
                            {renderDiagramPdf(pngLinks, diagramSize, diagrams, diagramLayout)}
                            {this.renderReportBemerkung()}
                        </>
                    }
                    {this.renderFooter()}
                </Page>
                {hasPage2 &&
                    <Page size='A4' orientation={this.props.orientation} style={pageStyle} key={`page-${page + 1}`}>
                        {renderBanner(this.state.config, styles, this.contextService, this.props.orientation)}
                        {renderDiagramPdf(pngLinks, diagramSize, diagrams, diagramLayout)}
                        {this.renderReportBemerkung()}
                        {this.renderFooter()}
                    </Page>
                }
                {hasPage3 &&
                    <Page size='A4' orientation={this.props.orientation} style={pageStyle} key={`page-${page + 2}`}>
                        {renderBanner(this.state.config, styles, this.contextService, this.props.orientation)}
                        {this.props.funcExternalElementExtrapage(true, this.state.config.values)}
                        {this.renderFooter()}
                    </Page>
                }
            </>;
        }
        await this.processPDF(pdfData);
        this.setState({ showSpinner: false, docType: this.props.setJSXdirect ? 'pdf' : null });
    }

    renderReportBemerkung(): JSX.Element {
        if (!this.report.active || typeof this.report.bemerkung !== 'string') {
            return null;
        }

        return (
            <View style={{ ...getStyle('bemerkung', true) }}>
                <Text>{this.report.bemerkung}</Text>
            </View>
        );
    }

    async renderDocx(pngLinks: any[]): Promise<void> {
        // console.log(this.props.config);
        const { filename, titel0, titel1, titel2 } = this.props.config;

        // Body
        const pngB64s = await Promise.all(pngLinks.map((url: string) => this.getImgBase64(url)));
        const body: React.ReactElement = <div>
            {renderDocxRows(this.props.config)}
            {renderDocxDiagram(this.props.config, pngB64s)}
        </div>;
        const bodyStr = ReactDOMServer.renderToString(body);

        // Header
        const logoUrl = require('../../img/Logo.jpg');
        const logoB64 = await this.getImgBase64(logoUrl);
        const header: React.ReactElement = <div>{renderDocxHeader(this.props.config, logoB64)}</div>;
        const headerStr = ReactDOMServer.renderToString(header);

        // Footer
        const footer: React.ReactElement = <div>{renderDocxFooter()}</div>;
        const footerStr = ReactDOMServer.renderToString(footer);

        const title = (titel0 ? titel0 + ' - ' : '') + (titel1 ? titel1 + ' - ' : '') + (titel2 ? titel2 : '');
        const data = await HTMLtoDOCX(bodyStr, headerStr, {
            orientation: this.props.orientation,
            title: title,
            subject: title,
            description: title,
            keywords: 'E-Tool',
            creator: 'E-Tool',
            lastModifiedBy: 'E-Tool',
            font: 'Arial, sans-serif',
            fontSize: '9pt',
            table: { row: { cantSplit: true } },
            header: true,
            footer: true,
            pageNumber: true,
            lang: 'de-DE'
        }, footerStr);
        const exportName = typeof filename === 'string' ? filename.toLocaleLowerCase().endsWith('.pdf') ? filename.substring(0, filename.length - 4) + '.docx' : filename : "export.docx";
        saveAs(data, exportName);
        this.setState({ showSpinner: false, docType: null });
        // console.log(bodyStr, headerStr, footerStr);
    }

    async getImgBase64(url: string) {
        const buffer = await fetch(url).then(res => res.arrayBuffer());
        const b64 = 'data:image/png;base64, ' + btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
        return b64
    }
}
